import { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements, Elements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';
import { getStripe } from '../../../lib/stripe';
import type { PaymentRequest } from '@stripe/stripe-js';
import { motion, AnimatePresence } from 'framer-motion';

interface ResonateModalProps {
  isOpen: boolean;
  onClose: () => void;
  artistId: string;
  artistName: string;
  displayColor: string;
  onSuccess?: () => void;
}

interface SavedCard {
  id: string;
  card_brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  cardholder_name: string;
  is_default: boolean;
  stripe_payment_method_id?: string;
}

const BRAND_LABEL: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  jcb: 'JCB',
  default: 'Card',
};

const PRESET_AMOUNTS = [500, 1000, 3000];

export function ResonateModal({
  isOpen,
  onClose,
  artistId,
  artistName,
  displayColor,
  onSuccess,
}: ResonateModalProps) {
  if (!isOpen) return null;

  return (
    <Elements stripe={getStripe()}>
      <ResonateModalInner
        isOpen={isOpen}
        onClose={onClose}
        artistId={artistId}
        artistName={artistName}
        displayColor={displayColor}
        onSuccess={onSuccess}
      />
    </Elements>
  );
}

function ResonateModalInner({
  isOpen,
  onClose,
  artistId,
  artistName,
  displayColor,
  onSuccess,
}: ResonateModalProps) {
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep] = useState<'select' | 'confirm' | 'processing' | 'success'>('select');
  const [selectedAmount, setSelectedAmount] = useState<number | null>(500);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [hasMessage, setHasMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);

  const [cardholderName, setCardholderName] = useState('');
  const [saveCardInfo, setSaveCardInfo] = useState(false);

  // ── Apple Pay / Google Pay 用の PaymentRequest ──
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  const finalAmount = selectedAmount === null
    ? parseInt(customAmount) || 0
    : selectedAmount;

  // ── PaymentRequest の初期化 ──
  useEffect(() => {
    if (!stripe || !isOpen || finalAmount < 100) return;

    const pr = stripe.paymentRequest({
      country: 'JP',
      currency: 'jpy',
      total: {
        label: `${artistName} への投げ銭`,
        amount: finalAmount,
      },
      requestPayerName: false,
      requestPayerEmail: false,
    });

    pr.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
      } else {
        setCanMakePayment(false);
      }
    });

    // ── 決済イベントハンドラ ──
    pr.on('paymentmethod', async (ev) => {
      if (!user) {
        ev.complete('fail');
        setError('ログインが必要です');
        return;
      }

      try {
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;

        // PaymentIntent 作成
        const intentRes = await fetch(
          `${supabaseUrl}/functions/v1/create-payment-intent`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              amount: finalAmount,
              userId: user.id,
              metadata: {
                paymentType: 'resonate',
                artistId,
                message: message.trim() || null,
              },
            }),
          }
        );

        if (!intentRes.ok) {
          const errData = await intentRes.json();
          throw new Error(errData.error || '決済インテントの作成に失敗しました');
        }

        const { clientSecret, paymentIntentId } = await intentRes.json();

        // 決済確定
        const { error: confirmError } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          ev.complete('fail');
          setError(confirmError.message || '決済に失敗しました');
          return;
        }

        // resonance_payments にメッセージ保存
        await saveResonancePayment(user.id, artistId, finalAmount, paymentIntentId, message.trim());

        await checkPatronStatus(user.id, artistId, artistName);

        ev.complete('success');
        setHasMessage(!!message.trim());
        setStep('success');
        setTimeout(() => { onSuccess?.(); }, 1500);
        setTimeout(() => { onClose(); }, 3500);
      } catch (err: any) {
        console.error('Express checkout failed:', err);
        ev.complete('fail');
        setError(err.message || '決済に失敗しました');
      }
    });

    return () => {
      // クリーンアップ（イベントリスナー解除）
      pr.off('paymentmethod');
    };
  }, [stripe, isOpen, finalAmount, artistName, user, artistId, message, onSuccess, onClose]);

  // ─── resonance_payments 保存 ───
  const saveResonancePayment = async (
    userId: string,
    targetArtistId: string,
    amount: number,
    paymentIntentId: string | null,
    msg: string
  ) => {
    try {
      await supabase.from('resonance_payments').insert({
        seeker_id: userId,
        artist_id: targetArtistId,
        amount,
        stripe_payment_intent_id: paymentIntentId || null,
        status: 'succeeded',
        message: msg || null,
      });
    } catch (err) {
      console.error('resonance_payments 保存エラー:', err);
    }
  };

  // ─── PATRON認定判定 ───
  const checkPatronStatus = async (userId: string, targetArtistId: string, targetArtistName: string) => {
    try {
      // 対象アーティストへの累計サポート額を取得
      const { data: payments, error: paymentError } = await supabase
        .from('resonance_payments')
        .select('amount')
        .eq('seeker_id', userId)
        .eq('artist_id', targetArtistId);

      if (paymentError) throw paymentError;

      // 累計額を計算
      const totalAmount = payments?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;

      // 10,000pt未満の場合は何もしない
      if (totalAmount < 10000) return;

      // すでに同じアーティストのPATRON認定通知を受け取っているかチェック
      const { data: existingMessage, error: checkError } = await supabase
        .from('artist_messages')
        .select('id')
        .eq('recipient_id', userId)
        .ilike('message', `%PATRON認定%${targetArtistId}%`)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') throw checkError;

      // すでに通知済みの場合は何もしない
      if (existingMessage) return;

      // PATRON認定通知を挿入
      const { error: insertError } = await supabase
        .from('artist_messages')
        .insert({
          recipient_id: userId,
          sender_name: targetArtistName,
          message: `【PATRON認定】深い共鳴に感謝します。私の公式ページの『CREDITS』に、あなたの名前を永遠に刻みました。[ARTIST_ID:${targetArtistId}]`,
          created_at: new Date().toISOString(),
        });

      if (insertError) throw insertError;

      console.log('✅ PATRON notification sent');
    } catch (err) {
      console.error('Error checking PATRON status:', err);
    }
  };

  // ── 金額変更時に PaymentRequest を更新 ──
  useEffect(() => {
    if (!paymentRequest || finalAmount < 100) return;

    paymentRequest.update({
      total: {
        label: `${artistName} への投げ銭`,
        amount: finalAmount,
      },
    });
  }, [paymentRequest, finalAmount, artistName]);

  useEffect(() => {
    if (!isOpen || !user) return;
    supabase
      .from('payment_methods')
      .select('id, card_brand, last4, exp_month, exp_year, cardholder_name, is_default, stripe_payment_method_id')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('is_default', { ascending: false })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setSavedCards(data as SavedCard[]);
          const def = data.find((c: SavedCard) => c.is_default) ?? data[0];
          setSelectedCardId(def.id);
          setUseNewCard(false);
        } else {
          setSavedCards([]);
          setSelectedCardId(null);
          setUseNewCard(true);
        }
      });
  }, [isOpen, user]);

  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setSelectedAmount(500);
      setCustomAmount('');
      setMessage('');
      setHasMessage(false);
      setError(null);
      setCardholderName('');
      setSaveCardInfo(false);
    }
  }, [isOpen]);

  const handleResonate = async () => {
    if (!user || !stripe || finalAmount < 100) {
      setError('金額は100円以上を指定してください');
      return;
    }

    setStep('processing');
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const intentRes = await fetch(
        `${supabaseUrl}/functions/v1/create-payment-intent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            amount: finalAmount,
            userId: user.id,
            metadata: {
              paymentType: 'resonate',
              artistId,
              message: message.trim() || null,
            },
          }),
        }
      );

      if (!intentRes.ok) {
        const errData = await intentRes.json();
        throw new Error(errData.error || '決済インテントの作成に失敗しました');
      }

      const { clientSecret, paymentIntentId } = await intentRes.json();

      if (!useNewCard && selectedCardId) {
        const card = savedCards.find((c) => c.id === selectedCardId);
        if (!card?.stripe_payment_method_id) throw new Error('カード情報が見つかりません');

        const { error: confirmError } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: card.stripe_payment_method_id,
        });
        if (confirmError) throw new Error(confirmError.message || '決済に失敗しました');
      } else {
        if (!elements) throw new Error('カード情報の入力フォームが見つかりません');

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) throw new Error('カード情報を入力してください');
        if (!cardholderName.trim()) throw new Error('カード名義を入力してください');

        const { error: confirmError, paymentMethod } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: { name: cardholderName.trim().toUpperCase() },
          },
        });
        if (confirmError) throw new Error(confirmError.message || '決済に失敗しました');

        if (saveCardInfo && paymentMethod) {
          const setupRes = await fetch(`${supabaseUrl}/functions/v1/create-setup-intent`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ user_id: user.id }),
          });

          if (setupRes.ok) {
            const { client_secret: setupSecret } = await setupRes.json();
            const { error: setupError, setupIntent } = await stripe.confirmCardSetup(setupSecret, {
              payment_method: {
                card: cardElement,
                billing_details: { name: cardholderName.trim().toUpperCase() },
              },
            });

            if (!setupError && setupIntent?.payment_method) {
              const pm = setupIntent.payment_method;
              const pmDetails = typeof pm === 'string' ? null : pm;
              await supabase.from('payment_methods').insert({
                user_id: user.id,
                stripe_payment_method_id: typeof pm === 'string' ? pm : pm.id,
                card_brand: pmDetails?.card?.brand || 'default',
                last4: pmDetails?.card?.last4 || '0000',
                exp_month: pmDetails?.card?.exp_month || 12,
                exp_year: pmDetails?.card?.exp_year || 2099,
                cardholder_name: cardholderName.trim().toUpperCase(),
                is_default: savedCards.length === 0,
                is_active: true,
              });
            }
          }
        }
      }

      // resonance_payments にメッセージ保存
      await saveResonancePayment(user.id, artistId, finalAmount, paymentIntentId || null, message.trim());

      await checkPatronStatus(user.id, artistId, artistName);

      setHasMessage(!!message.trim());
      setStep('success');
      setTimeout(() => { onSuccess?.(); }, 1500);
      setTimeout(() => { onClose(); }, 3500);
    } catch (err: any) {
      console.error('Resonance payment failed:', err);
      setError(err.message || '投げ銭に失敗しました');
      setStep('confirm');
    }
  };

  const selectedCard = savedCards.find((c) => c.id === selectedCardId) ?? null;

  // ── 単一のモーダルシェル（モバイル・PC共通）──
  // CardElement の重複を防ぐため、コンテンツは1箇所のみレンダリングする
  return (
    <>
      {/* オーバーレイ */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 backdrop-blur-xl"
        style={{ zIndex: 9998 }}
        onClick={onClose}
      />

      {/* モーダル本体 */}
      <div
        className="fixed inset-0 flex items-end sm:items-center justify-center sm:p-4"
        style={{ zIndex: 9999 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          className="w-full sm:max-w-md flex flex-col rounded-t-2xl sm:rounded-2xl border"
          style={{
            background: 'rgba(8, 8, 10, 0.95)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            borderColor: `${displayColor}25`,
            boxShadow: `0 0 80px ${displayColor}18, 0 32px 64px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)`,
            maxHeight: '92vh',
          }}
        >
          {/* ヘッダー */}
          <div
            className="px-5 py-3 border-b flex-shrink-0 rounded-t-2xl sm:rounded-t-2xl"
            style={{
              background: `linear-gradient(135deg, ${displayColor}12, transparent 60%)`,
              borderColor: `${displayColor}18`,
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `${displayColor}18`,
                    border: `1px solid ${displayColor}35`,
                    boxShadow: `0 0 12px ${displayColor}20`,
                  }}
                >
                  <i className="ri-heart-3-line text-base" style={{ color: displayColor }} />
                </div>
                <div>
                  <h3 className="text-white font-bold text-base tracking-wider">投げ銭</h3>
                  <p className="text-gray-500 text-[10px] tracking-wider">RESONATE</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/5 transition-all cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
          </div>

          {/* コンテンツ */}
          <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
            <ModalContent
              step={step}
              artistName={artistName}
              selectedAmount={selectedAmount}
              setSelectedAmount={setSelectedAmount}
              customAmount={customAmount}
              setCustomAmount={setCustomAmount}
              message={message}
              setMessage={setMessage}
              hasMessage={hasMessage}
              finalAmount={finalAmount}
              displayColor={displayColor}
              error={error}
              savedCards={savedCards}
              selectedCardId={selectedCardId}
              setSelectedCardId={setSelectedCardId}
              useNewCard={useNewCard}
              setUseNewCard={setUseNewCard}
              selectedCard={selectedCard}
              cardholderName={cardholderName}
              setCardholderName={setCardholderName}
              saveCardInfo={saveCardInfo}
              setSaveCardInfo={setSaveCardInfo}
              paymentRequest={paymentRequest}
              canMakePayment={canMakePayment}
            />
          </div>

          {/* フッター */}
          <div
            className="px-5 py-3 flex-shrink-0 border-t"
            style={{
              borderColor: `${displayColor}12`,
              background: 'rgba(8,8,10,0.98)',
            }}
          >
            <ModalFooter
              step={step}
              displayColor={displayColor}
              onClose={onClose}
              onNext={() => setStep('confirm')}
              onBack={() => setStep('select')}
              onResonate={handleResonate}
              disabled={finalAmount < 100}
              savedCards={savedCards}
              selectedCardId={selectedCardId}
              useNewCard={useNewCard}
              saveCardInfo={saveCardInfo}
            />
          </div>
        </motion.div>
      </div>
    </>
  );
}

/* ── Content ── */
interface ContentProps {
  step: 'select' | 'confirm' | 'processing' | 'success';
  artistName: string;
  selectedAmount: number | null;
  setSelectedAmount: (n: number | null) => void;
  customAmount: string;
  setCustomAmount: (v: string) => void;
  message: string;
  setMessage: (v: string) => void;
  hasMessage: boolean;
  finalAmount: number;
  displayColor: string;
  error: string | null;
  savedCards: SavedCard[];
  selectedCardId: string | null;
  setSelectedCardId: (id: string) => void;
  useNewCard: boolean;
  setUseNewCard: (v: boolean) => void;
  selectedCard: SavedCard | null;
  cardholderName: string;
  setCardholderName: (v: string) => void;
  saveCardInfo: boolean;
  setSaveCardInfo: (v: boolean) => void;
  paymentRequest: PaymentRequest | null;
  canMakePayment: boolean;
}

function NewCardInputForm({
  displayColor,
  cardholderName,
  setCardholderName,
  saveCardInfo,
  setSaveCardInfo,
}: {
  displayColor: string;
  cardholderName: string;
  setCardholderName: (v: string) => void;
  saveCardInfo: boolean;
  setSaveCardInfo: (v: boolean) => void;
}) {
  return (
    <div
      className="rounded-xl border p-4 space-y-3 mt-2"
      style={{ background: 'rgba(255,255,255,0.02)', borderColor: `${displayColor}20` }}
    >
      <div>
        <label className="block text-gray-400 text-xs tracking-wider mb-1.5">カード情報</label>
        <div
          className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3"
          style={{
            '--stripe-color-text': '#ffffff',
            '--stripe-color-text-placeholder': '#6b7280',
            '--stripe-color-background': 'transparent',
            '--stripe-color-danger': '#ef4444',
          } as React.CSSProperties}
        >
          <CardElement
            options={{
              style: {
                base: {
                  color: '#ffffff',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: '14px',
                  letterSpacing: '0.025em',
                  '::placeholder': { color: '#6b7280' },
                },
                invalid: { color: '#ef4444' },
              },
              hidePostalCode: true,
            }}
          />
        </div>
        <p className="text-gray-500 text-[10px] mt-1 tracking-wider">
          ※ブラウザに保存されたカード情報の自動入力が可能です
        </p>
      </div>

      <div>
        <label className="block text-gray-400 text-xs tracking-wider mb-1.5">カード名義（ローマ字）</label>
        <input
          type="text"
          name="ccname"
          autoComplete="cc-name"
          placeholder="TARO YAMADA"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value.toUpperCase())}
          className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm tracking-widest placeholder-gray-700 focus:outline-none focus:border-white/20 transition-all"
        />
      </div>

      <button
        type="button"
        onClick={() => setSaveCardInfo(!saveCardInfo)}
        className="w-full flex items-center gap-3 p-4 rounded-xl border transition-all cursor-pointer text-left"
        style={{
          background: saveCardInfo ? `${displayColor}08` : 'rgba(255,255,255,0.01)',
          borderColor: saveCardInfo ? `${displayColor}30` : 'rgba(255,255,255,0.08)',
        }}
      >
        <div
          className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-all border-2"
          style={{
            background: saveCardInfo ? displayColor : 'transparent',
            borderColor: saveCardInfo ? displayColor : 'rgba(255,255,255,0.2)',
          }}
        >
          {saveCardInfo && <i className="ri-check-line text-xs text-black font-bold" />}
        </div>
        <div className="flex-1">
          <p className="text-white text-sm font-bold tracking-wider mb-1">この決済情報を深層に刻み、次回から摩擦ゼロで共鳴する</p>
          <p className="text-gray-500 text-[10px] mt-0.5 tracking-wide">カード情報はStripeにより安全に暗号化・保管されます</p>
        </div>
      </button>

      <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-white/[0.02] border border-white/5">
        <i className="ri-shield-check-line text-green-400 text-xs mt-0.5 flex-shrink-0" />
        <p className="text-gray-600 text-[10px] leading-relaxed">
          カード情報は Stripe により安全に暗号化されて処理されます。
        </p>
      </div>
    </div>
  );
}

function ModalContent({
  step,
  artistName,
  selectedAmount,
  setSelectedAmount,
  customAmount,
  setCustomAmount,
  message,
  setMessage,
  hasMessage,
  finalAmount,
  displayColor,
  error,
  savedCards,
  selectedCardId,
  setSelectedCardId,
  useNewCard,
  setUseNewCard,
  cardholderName,
  setCardholderName,
  saveCardInfo,
  setSaveCardInfo,
  paymentRequest,
  canMakePayment,
}: ContentProps) {
  const [messageFocused, setMessageFocused] = useState(false);
  const [customFocused, setCustomFocused] = useState(false);

  return (
    <>
      {step === 'select' && (
        <div className="space-y-4">
          {/* アーティスト表示 */}
          <div
            className="p-3 rounded-xl border"
            style={{
              background: 'rgba(255,255,255,0.02)',
              backdropFilter: 'blur(12px)',
              borderColor: 'rgba(255,255,255,0.06)',
            }}
          >
            <p className="text-gray-500 text-xs tracking-wider mb-1">アーティスト</p>
            <p className="text-white text-sm font-bold tracking-wide">{artistName}</p>
          </div>

          {/* 金額選択 */}
          <div>
            <label className="text-gray-400 text-sm tracking-wider mb-2 block">応援金額</label>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {PRESET_AMOUNTS.map((amount) => {
                const isSelected = selectedAmount === amount;
                return (
                  <motion.button
                    key={amount}
                    onClick={() => { setSelectedAmount(amount); setCustomAmount(''); }}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="py-[0.809rem] rounded-xl border transition-all duration-200 cursor-pointer relative overflow-hidden"
                    style={{
                      background: isSelected
                        ? `linear-gradient(135deg, ${displayColor}20, ${displayColor}08)`
                        : 'rgba(255,255,255,0.02)',
                      borderColor: isSelected ? `${displayColor}60` : 'rgba(255,255,255,0.07)',
                      boxShadow: isSelected
                        ? `0 0 20px ${displayColor}30, 0 0 40px ${displayColor}12, inset 0 1px 0 ${displayColor}20`
                        : 'none',
                    }}
                  >
                    {isSelected && (
                      <motion.div
                        layoutId="amountGlow"
                        className="absolute inset-0 rounded-xl pointer-events-none"
                        style={{
                          background: `radial-gradient(ellipse at center, ${displayColor}15, transparent 70%)`,
                        }}
                      />
                    )}
                    <div
                      className="text-lg font-bold relative z-10 transition-colors duration-200"
                      style={{ color: isSelected ? displayColor : 'rgba(255,255,255,0.85)' }}
                    >
                      ¥{amount.toLocaleString()}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* カスタム金額 */}
            <div>
              <label className="text-gray-400 text-xs tracking-wider mb-1.5 block">カスタム金額（100円以上）</label>
              <div
                className="relative rounded-xl transition-all duration-300"
                style={{
                  boxShadow: customFocused
                    ? `0 0 0 1px ${displayColor}50, 0 0 16px ${displayColor}18`
                    : '0 0 0 1px rgba(255,255,255,0.06)',
                }}
              >
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">¥</span>
                <input
                  type="number"
                  min="100"
                  placeholder="1000"
                  value={customAmount}
                  onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                  onFocus={() => setCustomFocused(true)}
                  onBlur={() => setCustomFocused(false)}
                  className="w-full bg-white/[0.03] rounded-xl pl-8 pr-4 py-[0.618rem] text-white text-sm tracking-wider placeholder-gray-700 focus:outline-none transition-all border-0"
                />
              </div>
            </div>
          </div>

          {/* メッセージ入力 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-gray-400 text-sm tracking-wider flex items-center gap-1.5">
                <i className="ri-message-3-line text-xs" style={{ color: messageFocused ? displayColor : undefined }} />
                アーティストへ一言
                <span className="text-gray-600 text-[10px] tracking-wide">（任意）</span>
              </label>
              <span
                className="text-[10px] font-bold tracking-wider transition-colors"
                style={{ color: message.length > 90 ? '#ef4444' : message.length > 70 ? '#f59e0b' : 'rgba(255,255,255,0.2)' }}
              >
                {message.length} / 100
              </span>
            </div>

            <div
              className="relative rounded-xl transition-all duration-300"
              style={{
                boxShadow: messageFocused
                  ? `0 0 0 1px ${displayColor}55, 0 0 20px ${displayColor}18, 0 0 40px ${displayColor}08`
                  : '0 0 0 1px rgba(255,255,255,0.06)',
              }}
            >
              <textarea
                placeholder="あなたの言葉がアーティストの力になります..."
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 100))}
                onFocus={() => setMessageFocused(true)}
                onBlur={() => setMessageFocused(false)}
                maxLength={100}
                rows={3}
                className="w-full rounded-xl px-4 py-[0.618rem] text-white text-sm tracking-wide placeholder-gray-600 focus:outline-none transition-all resize-none border-0"
                style={{
                  background: messageFocused ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.02)',
                  backdropFilter: 'blur(8px)',
                }}
              />
              {messageFocused && (
                <div
                  className="absolute bottom-0 left-4 right-4 h-px rounded-full pointer-events-none"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${displayColor}, transparent)`,
                    boxShadow: `0 0 8px ${displayColor}`,
                  }}
                />
              )}
            </div>

            <AnimatePresence>
              {message.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="flex items-center gap-1.5 mt-1.5 px-2"
                >
                  <i className="ri-sparkling-line text-[10px]" style={{ color: displayColor }} />
                  <span className="text-[10px] tracking-wider" style={{ color: `${displayColor}99` }}>
                    このメッセージはアーティストに届きます
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* 金額サマリー */}
          <div
            className="p-[0.809rem] rounded-xl border"
            style={{
              background: `linear-gradient(135deg, ${displayColor}08, transparent)`,
              borderColor: `${displayColor}20`,
              backdropFilter: 'blur(12px)',
            }}
          >
            <div className="flex items-center justify-between">
              <span className="text-white text-sm font-bold tracking-wider">応援金額</span>
              <motion.span
                key={finalAmount}
                initial={{ scale: 1.15, opacity: 0.7 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-2xl font-bold tracking-wider"
                style={{ color: displayColor }}
              >
                ¥{finalAmount.toLocaleString()}
              </motion.span>
            </div>
          </div>

          {/* Apple Pay / Google Pay */}
          {canMakePayment && paymentRequest && finalAmount >= 100 && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: displayColor, boxShadow: `0 0 6px ${displayColor}` }} />
                  <span className="text-gray-500 text-[10px] font-bold tracking-[0.25em]">QUICK RESONATE</span>
                  <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: displayColor, boxShadow: `0 0 6px ${displayColor}`, animationDelay: '0.5s' }} />
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>
              <div className="relative group">
                <div className="absolute -inset-[2px] rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `linear-gradient(135deg, ${displayColor}40, transparent, ${displayColor}30)`, filter: 'blur(4px)' }} />
                <div className="absolute -inset-[3px] rounded-xl animate-ping opacity-20" style={{ border: `1px solid ${displayColor}`, animationDuration: '2.5s' }} />
                <div className="relative rounded-xl overflow-hidden transition-all duration-300 group-hover:scale-[1.01]" style={{ boxShadow: `0 0 24px ${displayColor}20, inset 0 1px 0 rgba(255,255,255,0.05)`, border: `1px solid ${displayColor}35` }}>
                  <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${displayColor}60, transparent)` }} />
                  <PaymentRequestButtonElement options={{ paymentRequest, style: { paymentRequestButton: { theme: 'dark', height: '52px', type: 'default' } } }} />
                  <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${displayColor}30, transparent)` }} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/8 to-transparent" />
                <span className="text-gray-700 text-[10px] font-bold tracking-[0.2em]">OR PAY WITH CARD</span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/8 to-transparent" />
              </div>
            </div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 rounded-xl bg-red-500/10 border border-red-500/30"
            >
              <div className="flex items-center gap-2">
                <i className="ri-error-warning-line text-red-500" />
                <span className="text-red-400 text-sm">{error}</span>
              </div>
            </motion.div>
          )}
        </div>
      )}

      {step === 'confirm' && (
        <div className="space-y-3">
          <div className="text-center py-2">
            <div
              className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
              style={{
                background: `${displayColor}18`,
                border: `2px solid ${displayColor}40`,
                boxShadow: `0 0 24px ${displayColor}20`,
              }}
            >
              <i className="ri-heart-3-fill text-2xl" style={{ color: displayColor }} />
            </div>
            <h4 className="text-white text-xl font-bold tracking-wider mb-2">投げ銭内容の確認</h4>
            <p className="text-gray-500 text-sm">以下の内容で送信します</p>
          </div>

          <div className="space-y-2">
            <div className="p-[0.618rem] rounded-xl border" style={{ background: 'rgba(255,255,255,0.02)', backdropFilter: 'blur(12px)', borderColor: 'rgba(255,255,255,0.06)' }}>
              <p className="text-gray-500 text-xs tracking-wider mb-0.5">アーティスト</p>
              <p className="text-white text-sm font-bold tracking-wide">{artistName}</p>
            </div>
            {message && (
              <div className="p-[0.618rem] rounded-xl border" style={{ background: 'rgba(255,255,255,0.02)', backdropFilter: 'blur(12px)', borderColor: 'rgba(255,255,255,0.06)' }}>
                <p className="text-gray-500 text-xs tracking-wider mb-0.5">メッセージ</p>
                <p className="text-white text-sm tracking-wide">{message}</p>
              </div>
            )}
            <div className="p-[0.809rem] rounded-xl border" style={{ background: `${displayColor}10`, borderColor: `${displayColor}30`, backdropFilter: 'blur(12px)' }}>
              <p className="text-gray-400 text-xs tracking-wider mb-0.5">応援金額</p>
              <p className="text-xl font-bold tracking-wider" style={{ color: displayColor }}>
                ¥{finalAmount.toLocaleString()}
              </p>
            </div>
          </div>

          <div>
            <p className="text-gray-400 text-xs tracking-wider mb-2">支払い方法</p>

            {savedCards.length > 0 && (
              <div className="space-y-2 mb-2">
                {savedCards.map((card) => {
                  const isSelected = !useNewCard && selectedCardId === card.id;
                  return (
                    <button
                      key={card.id}
                      onClick={() => { setSelectedCardId(card.id); setUseNewCard(false); }}
                      className="w-full flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer text-left relative"
                      style={{
                        background: isSelected ? `${displayColor}10` : 'rgba(255,255,255,0.02)',
                        borderColor: isSelected ? displayColor : 'rgba(255,255,255,0.08)',
                        boxShadow: isSelected ? `0 0 20px ${displayColor}35, inset 0 0 20px ${displayColor}08` : 'none',
                        backdropFilter: 'blur(12px)',
                      }}
                    >
                      <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all" style={{ borderColor: isSelected ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)', background: isSelected ? 'rgba(255,255,255,0.8)' : 'transparent' }}>
                        {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
                      </div>
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <i className="ri-bank-card-line text-base text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-bold tracking-wider">
                            {(BRAND_LABEL as Record<string, string>)[card.card_brand] ?? 'Card'} •••• {card.last4}
                          </span>
                          {card.is_default && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold tracking-wider" style={{ background: `${displayColor}20`, color: displayColor, border: `1px solid ${displayColor}40` }}>DEFAULT</span>
                          )}
                        </div>
                        <p className="text-gray-500 text-[10px] tracking-wider">{card.cardholder_name} &nbsp;|&nbsp; {String(card.exp_month).padStart(2, '0')}/{String(card.exp_year).slice(-2)}</p>
                      </div>
                      {isSelected && (
                        <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                          <i className="ri-check-line text-sm" style={{ color: displayColor }} />
                        </div>
                      )}
                    </button>
                  );
                })}

                <button
                  onClick={() => setUseNewCard(true)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer text-left"
                  style={{ background: useNewCard ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.01)', borderColor: useNewCard ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)' }}
                >
                  <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all" style={{ borderColor: useNewCard ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)', background: useNewCard ? 'rgba(255,255,255,0.8)' : 'transparent' }}>
                    {useNewCard && <div className="w-1.5 h-1.5 rounded-full bg-black" />}
                  </div>
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <i className="ri-add-line text-base text-gray-500" />
                  </div>
                  <span className="text-gray-400 text-sm tracking-wider">別のカードで支払う</span>
                </button>
              </div>
            )}

            {(savedCards.length === 0 || useNewCard) && (
              <NewCardInputForm
                displayColor={displayColor}
                cardholderName={cardholderName}
                setCardholderName={setCardholderName}
                saveCardInfo={saveCardInfo}
                setSaveCardInfo={setSaveCardInfo}
              />
            )}
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 rounded-xl bg-red-500/10 border border-red-500/30"
            >
              <div className="flex items-center gap-2">
                <i className="ri-error-warning-line text-red-500" />
                <span className="text-red-400 text-sm">{error}</span>
              </div>
            </motion.div>
          )}
        </div>
      )}

      {step === 'processing' && (
        <div className="py-12 text-center">
          <motion.div
            animate={{ scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center"
            style={{
              background: `${displayColor}18`,
              border: `2px solid ${displayColor}40`,
              boxShadow: `0 0 40px ${displayColor}25`,
            }}
          >
            <i className="ri-loader-4-line text-3xl animate-spin" style={{ color: displayColor }} />
          </motion.div>
          <h4 className="text-white text-xl font-bold tracking-wider mb-2">処理中...</h4>
          <p className="text-gray-500 text-sm">しばらくお待ちください</p>
        </div>
      )}

      {step === 'success' && (
        <SuccessView
          displayColor={displayColor}
          finalAmount={finalAmount}
          message={message}
          hasMessage={hasMessage}
          saveCardInfo={saveCardInfo}
          savedCards={savedCards}
          useNewCard={useNewCard}
        />
      )}
    </>
  );
}

/* ── 成功画面コンポーネント（強化版） ── */
function SuccessView({
  displayColor,
  finalAmount,
  message,
  hasMessage,
  saveCardInfo,
  savedCards,
  useNewCard,
}: {
  displayColor: string;
  finalAmount: number;
  message: string;
  hasMessage: boolean;
  saveCardInfo: boolean;
  savedCards: SavedCard[];
  useNewCard: boolean;
}) {
  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowMessage(true), 700);
    return () => clearTimeout(t);
  }, []);

  // パーティクル設定（放射状 + ランダム浮遊）
  const particles = Array.from({ length: 20 }, (_, i) => {
    const angle = (i / 20) * Math.PI * 2;
    const radius = 55 + (i % 3) * 18;
    const size = i % 4 === 0 ? 3 : i % 3 === 0 ? 2 : 1.5;
    const color = i % 4 === 0 ? displayColor : i % 4 === 1 ? '#ffffff' : i % 4 === 2 ? `${displayColor}cc` : '#ffffffaa';
    return { angle, radius, size, color, delay: i * 0.04 };
  });

  // 星型スパークル
  const sparkles = Array.from({ length: 8 }, (_, i) => ({
    x: 20 + (i * 9.5) % 60,
    y: 10 + (i * 13) % 80,
    delay: 0.3 + i * 0.12,
    scale: 0.6 + (i % 3) * 0.3,
  }));

  return (
    <div className="py-8 text-center relative overflow-hidden">
      {/* 背景グロー */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 50% 35%, ${displayColor}12 0%, transparent 65%)`,
        }}
      />

      {/* 放射パーティクル */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0, x: '50%', y: '35%' }}
            animate={{
              opacity: [0, 1, 0.8, 0],
              scale: [0, 1.2, 1, 0.4],
              x: `calc(50% + ${Math.cos(p.angle) * p.radius}%)`,
              y: `calc(35% + ${Math.sin(p.angle) * p.radius}%)`,
            }}
            transition={{ duration: 1.4, delay: p.delay, ease: [0.2, 0.8, 0.4, 1] }}
            className="absolute rounded-full"
            style={{
              width: p.size,
              height: p.size,
              background: p.color,
              boxShadow: `0 0 ${p.size * 3}px ${p.color}`,
              left: 0,
              top: 0,
            }}
          />
        ))}

        {/* スパークル（星型） */}
        {sparkles.map((s, i) => (
          <motion.div
            key={`spark-${i}`}
            initial={{ opacity: 0, scale: 0, rotate: 0 }}
            animate={{ opacity: [0, 1, 0], scale: [0, s.scale, 0], rotate: [0, 180] }}
            transition={{ duration: 0.9, delay: s.delay, ease: 'easeOut' }}
            className="absolute"
            style={{ left: `${s.x}%`, top: `${s.y}%` }}
          >
            <i className="ri-sparkling-2-fill text-xs" style={{ color: displayColor }} />
          </motion.div>
        ))}
      </div>

      {/* メインアイコン */}
      <motion.div
        initial={{ scale: 0, rotate: -30 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 18, delay: 0.08 }}
        className="w-20 h-20 rounded-2xl mx-auto mb-5 flex items-center justify-center relative"
        style={{
          background: hasMessage
            ? `linear-gradient(135deg, ${displayColor}28, ${displayColor}12)`
            : 'linear-gradient(135deg, rgba(0,255,65,0.18), rgba(0,255,65,0.06))',
          border: hasMessage ? `2px solid ${displayColor}55` : '2px solid rgba(0,255,65,0.35)',
          boxShadow: hasMessage
            ? `0 0 50px ${displayColor}30, 0 0 100px ${displayColor}12`
            : '0 0 40px rgba(0,255,65,0.22)',
        }}
      >
        <motion.i
          initial={{ scale: 0, rotate: 20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.25, type: 'spring', stiffness: 380, damping: 16 }}
          className={`text-4xl ${hasMessage ? 'ri-message-3-fill' : 'ri-check-line'}`}
          style={{ color: hasMessage ? displayColor : '#00ff41' }}
        />
        {/* 第1リング */}
        <motion.div
          initial={{ scale: 0.8, opacity: 1 }}
          animate={{ scale: 2.2, opacity: 0 }}
          transition={{ duration: 1.0, delay: 0.3, ease: 'easeOut' }}
          className="absolute inset-0 rounded-2xl border-2 pointer-events-none"
          style={{ borderColor: hasMessage ? displayColor : '#00ff41' }}
        />
        {/* 第2リング（遅延） */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0.7 }}
          animate={{ scale: 2.8, opacity: 0 }}
          transition={{ duration: 1.3, delay: 0.5, ease: 'easeOut' }}
          className="absolute inset-0 rounded-2xl border pointer-events-none"
          style={{ borderColor: hasMessage ? `${displayColor}80` : 'rgba(0,255,65,0.5)' }}
        />
      </motion.div>

      {/* タイトル */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.32, type: 'spring', stiffness: 200 }}
      >
        {hasMessage ? (
          <>
            <h4 className="text-white text-xl font-bold tracking-wider mb-1">Message Sent</h4>
            <p className="font-bold tracking-widest text-sm mb-1" style={{ color: displayColor }}>共鳴が届きました</p>
          </>
        ) : (
          <h4 className="text-white text-xl font-bold tracking-wider mb-2">投げ銭完了！</h4>
        )}
        <p className="text-gray-500 text-xs tracking-wider">
          {hasMessage ? 'あなたの言葉とともに、想いが届きました' : 'アーティストへの応援が完了しました'}
        </p>
      </motion.div>

      {/* 金額バッジ */}
      <motion.div
        initial={{ opacity: 0, scale: 0.75 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.48, type: 'spring', stiffness: 260, damping: 18 }}
        className="mt-4 inline-flex items-center gap-2 px-5 py-[0.618rem] rounded-full"
        style={{
          background: hasMessage ? `${displayColor}15` : 'rgba(0,255,65,0.08)',
          border: `1px solid ${hasMessage ? `${displayColor}40` : 'rgba(0,255,65,0.28)'}`,
          boxShadow: `0 0 20px ${hasMessage ? `${displayColor}20` : 'rgba(0,255,65,0.15)'}`,
        }}
      >
        <i className="ri-heart-3-fill" style={{ color: hasMessage ? displayColor : '#00ff41' }} />
        <span className="text-sm font-bold" style={{ color: hasMessage ? displayColor : '#00ff41' }}>
          ¥{finalAmount.toLocaleString()}
        </span>
      </motion.div>

      {/* メッセージプレビュー */}
      <AnimatePresence>
        {hasMessage && showMessage && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
            className="mt-4 mx-2 p-[0.809rem] rounded-xl text-left"
            style={{
              background: `${displayColor}08`,
              border: `1px solid ${displayColor}20`,
              backdropFilter: 'blur(8px)',
            }}
          >
            <div className="flex items-center gap-1.5 mb-1.5">
              <i className="ri-double-quotes-l text-xs" style={{ color: `${displayColor}80` }} />
              <span className="text-[10px] tracking-widest font-bold" style={{ color: `${displayColor}80` }}>YOUR MESSAGE</span>
            </div>
            <p className="text-gray-300 text-xs leading-relaxed tracking-wide italic">{message}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {saveCardInfo && (savedCards.length === 0 || useNewCard) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.75 }}
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10"
        >
          <i className="ri-bank-card-line text-gray-400 text-sm" />
          <span className="text-gray-400 text-xs">カード情報を保存しました</span>
        </motion.div>
      )}
    </div>
  );
}

/* ── Footer ── */
interface FooterProps {
  step: 'select' | 'confirm' | 'processing' | 'success';
  displayColor: string;
  onClose: () => void;
  onNext: () => void;
  onBack: () => void;
  onResonate: () => void;
  disabled: boolean;
  savedCards: SavedCard[];
  selectedCardId: string | null;
  useNewCard: boolean;
  saveCardInfo: boolean;
}

function ModalFooter({
  step,
  displayColor,
  onClose,
  onNext,
  onBack,
  onResonate,
  disabled,
  savedCards,
  selectedCardId,
  useNewCard,
  saveCardInfo,
}: FooterProps) {
  if (step !== 'select' && step !== 'confirm') return null;

  let resonateButtonText = '共鳴する';
  if (step === 'confirm') {
    if (!useNewCard && selectedCardId && savedCards.length > 0) {
      resonateButtonText = '1-TAP RESONATE';
    } else if (useNewCard || savedCards.length === 0) {
      resonateButtonText = saveCardInfo ? '刻印して共鳴する' : '共鳴する';
    }
  }

  return (
    <div className="flex gap-3">
      {step === 'select' && (
        <>
          <button
            onClick={onClose}
            className="flex-1 py-[0.809rem] rounded-xl border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 transition-all cursor-pointer whitespace-nowrap text-sm"
          >
            キャンセル
          </button>
          <motion.button
            onClick={onNext}
            disabled={disabled}
            whileHover={!disabled ? { scale: 1.02 } : {}}
            whileTap={!disabled ? { scale: 0.98 } : {}}
            className="flex-1 py-[0.809rem] rounded-xl font-bold tracking-wider transition-all cursor-pointer whitespace-nowrap text-sm disabled:opacity-30 disabled:cursor-not-allowed"
            style={{
              background: disabled ? 'rgba(255,255,255,0.1)' : `linear-gradient(135deg, ${displayColor}, ${displayColor}cc)`,
              color: disabled ? 'rgba(255,255,255,0.3)' : '#000',
              boxShadow: disabled ? 'none' : `0 0 30px ${displayColor}35, 0 4px 16px ${displayColor}20`,
            }}
          >
            次へ
          </motion.button>
        </>
      )}
      {step === 'confirm' && (
        <>
          <button
            onClick={onBack}
            className="flex-1 py-[0.809rem] rounded-xl border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 transition-all cursor-pointer whitespace-nowrap text-sm"
          >
            戻る
          </button>
          <motion.button
            onClick={onResonate}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 py-[0.809rem] rounded-xl font-bold tracking-wider transition-all cursor-pointer whitespace-nowrap text-sm"
            style={{
              background: `linear-gradient(135deg, ${displayColor}, ${displayColor}cc)`,
              color: '#000',
              boxShadow: `0 0 30px ${displayColor}35, 0 4px 16px ${displayColor}20`,
            }}
          >
            {resonateButtonText}
          </motion.button>
        </>
      )}
    </div>
  );
}
